import { z } from 'zod';
import { PermissionKeySchema } from './permissions';

/* ----------------------------- secret format ----------------------------- */

/**
 * Secret layout: `stk_<tenant id as 32 hex chars>_<32 base62 chars>`. The `api_key` table is
 * under RLS and the API runs as `app_rw`, so the token itself must carry the tenant: the
 * middleware derives `tenant_id` from it, looks the row up by prefix inside `withTenant` and
 * compares the SHA-256 of the whole token (ADR-0015 §9).
 */
export const API_KEY_PREFIX = 'stk_';
/** Tenant id without dashes. */
export const API_KEY_TENANT_HEX_LENGTH = 32;
/** Base62 characters of the random part (ADR-0015 §9). */
export const API_KEY_SECRET_LENGTH = 32;
/** First characters of the random part, stored as `api_key.key_prefix` to narrow the lookup. */
export const API_KEY_LOOKUP_PREFIX_LENGTH = 12;
export const API_KEY_PATTERN = /^stk_[0-9a-f]{32}_[0-9A-Za-z]{32}$/;
export const API_KEY_BASE62 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

export const ApiKeySecretSchema = z
  .string()
  .regex(
    API_KEY_PATTERN,
    'API key: stk_, the tenant id as 32 hex digits, _ and 32 letters or digits',
  );
export type ApiKeySecret = z.infer<typeof ApiKeySecretSchema>;

export const isApiKeySecret = (value: string): boolean => API_KEY_PATTERN.test(value);

export interface ParsedApiKeySecret {
  tenantId: string;
  /** The 32 base62 characters. */
  random: string;
  /** `random.slice(0, 12)`, what `api_key.key_prefix` stores. */
  lookupPrefix: string;
}

const hexToUuid = (hex: string): string =>
  `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;

/** Splits a well-formed secret into its parts; null when the shape is wrong. */
export function parseApiKeySecret(secret: string): ParsedApiKeySecret | null {
  if (!API_KEY_PATTERN.test(secret)) {
    return null;
  }
  const body = secret.slice(API_KEY_PREFIX.length);
  const hex = body.slice(0, API_KEY_TENANT_HEX_LENGTH);
  const random = body.slice(API_KEY_TENANT_HEX_LENGTH + 1);
  return {
    tenantId: hexToUuid(hex),
    random,
    lookupPrefix: random.slice(0, API_KEY_LOOKUP_PREFIX_LENGTH),
  };
}

/** Builds the secret from its parts; `random` must be 32 base62 characters. */
export const formatApiKeySecret = (tenantId: string, random: string): string =>
  `${API_KEY_PREFIX}${tenantId.replace(/-/g, '').toLowerCase()}_${random}`;

/** The stored lookup prefix of a secret (first 12 characters of its random part). */
export const apiKeyLookupPrefix = (secret: string): string =>
  parseApiKeySecret(secret)?.lookupPrefix ?? secret.slice(0, API_KEY_LOOKUP_PREFIX_LENGTH);

/* ----------------------------- scopes ----------------------------- */

/** Pseudo-scope for the configuration endpoints (tenant admin level). */
export const API_KEY_ADMIN_SCOPE = 'admin';
export const ApiKeyScopeSchema = z.union([PermissionKeySchema, z.literal(API_KEY_ADMIN_SCOPE)]);
export type ApiKeyScope = z.infer<typeof ApiKeyScopeSchema>;

export const API_KEY_SCOPES_MAX = 30;
export const API_KEY_PROJECTS_MAX = 100;

const ScopesSchema = z
  .array(ApiKeyScopeSchema)
  .min(1)
  .max(API_KEY_SCOPES_MAX)
  .superRefine((scopes, ctx) => {
    const seen = new Set<string>();
    scopes.forEach((scope, i) => {
      if (seen.has(scope)) {
        ctx.addIssue({ code: 'custom', message: `duplicate scope ${scope}`, path: [i] });
      }
      seen.add(scope);
    });
  });

/* ----------------------------- DTOs ----------------------------- */

/**
 * `POST /admin/v1/api-keys` (S-05-15). `projectIds` null = every project the service user
 * can reach; `expiresAt` null = never.
 */
export const CreateApiKeySchema = z.strictObject({
  name: z.string().trim().min(1).max(80),
  scopes: ScopesSchema,
  projectIds: z.array(z.uuid()).min(1).max(API_KEY_PROJECTS_MAX).nullable().default(null),
  expiresAt: z.iso.datetime({ offset: true }).nullable().default(null),
});
export type CreateApiKey = z.infer<typeof CreateApiKeySchema>;
export type CreateApiKeyInput = z.input<typeof CreateApiKeySchema>;

/** Never carries the secret; `keyPrefix` lets admins recognise a key in logs. */
export const ApiKeySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  keyPrefix: z.string(),
  scopes: z.array(ApiKeyScopeSchema),
  projectIds: z.array(z.uuid()).nullable(),
  serviceUserId: z.uuid(),
  createdBy: z.uuid(),
  lastUsedAt: z.iso.datetime().nullable(),
  expiresAt: z.iso.datetime().nullable(),
  revokedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ApiKey = z.infer<typeof ApiKeySchema>;

/** 201 body: the only time the secret is shown. */
export const ApiKeyCreatedSchema = ApiKeySchema.extend({ secret: ApiKeySecretSchema });
export type ApiKeyCreated = z.infer<typeof ApiKeyCreatedSchema>;
